import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, Truck, ShieldCheck, Phone, MessageCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { CategoryCard } from '@/components/CategoryCard';
import { ProductCard } from '@/components/ProductCard';
import { CategorySkeleton, ProductSkeleton } from '@/components/Skeletons';
import { getCategories, getPopularProducts, seedData } from '@/lib/api';

const SUPPORT_PHONE = '+919603540072';
const WHATSAPP_LINK = `https://wa.me/919603540072?text=${encodeURIComponent('Hi! I want to order from QUICK GRAM TURA.')}`;

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await seedData();
        const [catRes, popRes] = await Promise.all([
          getCategories(),
          getPopularProducts(10),
        ]);
        setCategories(catRes.data);
        setPopular(popRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <CustomerLayout>
      {/* Hero */}
      <section className="relative bg-primary overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200"
            alt="Fresh groceries"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
        <div className="relative px-4 py-10 md:py-16 max-w-7xl mx-auto">
          <div className="max-w-xl">
            <p className="text-accent font-bold text-base md:text-lg mb-2">🚜 QUICK GRAM TURA — మీ ఊరికి డెలివరీ</p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3 leading-tight">
              Groceries in <span className="text-accent">30 minutes</span>
            </h1>
            <p className="text-white/95 text-base md:text-lg mb-5 font-medium">
              Fresh vegetables, dairy & daily essentials delivered to your doorstep. Cash on Delivery.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-order-btn">
                <Button className="h-14 px-7 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold text-base shadow-lg">
                  Order Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
                <Button className="h-14 px-6 rounded-full bg-[#25D366] hover:bg-[#1ebd5b] text-white font-bold text-base shadow-lg">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Support Strip */}
      <section className="bg-accent/15 border-b border-accent/30" data-testid="support-strip">
        <div className="px-4 py-3 max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm md:text-base font-semibold text-foreground">
            Need help? Talk to us — we speak Telugu & Hindi.
          </p>
          <a
            href={`tel:${SUPPORT_PHONE}`}
            data-testid="call-support-btn"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 font-bold text-sm md:text-base shadow-sm hover:bg-primary/90 transition"
          >
            <Phone className="w-4 h-4" />
            Call {SUPPORT_PHONE}
          </a>
        </div>
      </section>

      {/* Delivery Info */}
      <section className="bg-white border-b border-border" data-testid="delivery-info">
        <div className="px-4 py-5 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-bold text-sm">30-45 mins</p>
                <p className="text-xs text-muted-foreground">Delivery</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-bold text-sm">10 km radius</p>
                <p className="text-xs text-muted-foreground">Coverage</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-bold text-sm">Free Delivery</p>
                <p className="text-xs text-muted-foreground">Orders ₹199+</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-bold text-sm">Cash on Delivery</p>
                <p className="text-xs text-muted-foreground">Pay at door</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 py-6 max-w-7xl mx-auto" data-testid="categories-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-extrabold">Shop by Category</h2>
          <Link to="/products" className="text-sm text-primary font-bold flex items-center gap-1">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-3 md:gap-4">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <CategorySkeleton key={i} />)
            : categories.map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
        </div>
      </section>

      {/* Frequently Bought */}
      <section className="px-4 py-6 max-w-7xl mx-auto" data-testid="frequently-bought-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star className="w-6 h-6 text-accent fill-accent" />
            <h2 className="text-xl md:text-2xl font-extrabold">Frequently Bought</h2>
          </div>
          <Link to="/products" className="text-sm text-primary font-bold flex items-center gap-1">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Most popular items in your area — quick reorder essentials</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)
            : popular.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 py-8 bg-muted/50" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-extrabold text-center mb-8">How QUICK GRAM TURA Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: 1, t: 'Browse & Add', d: 'Pick items from categories — see Telugu names & prices clearly' },
              { n: 2, t: 'Checkout', d: 'Enter address & phone. Choose Cash on Delivery — no online payment needed' },
              { n: 3, t: 'Get Delivered', d: 'Receive at your doorstep within 30–45 minutes. Pay cash to delivery person' },
            ].map((s) => (
              <div key={s.n} className="text-center bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-16 h-16 rounded-full bg-primary text-white font-extrabold text-2xl flex items-center justify-center mx-auto mb-4">
                  {s.n}
                </div>
                <h3 className="font-bold text-lg mb-2">{s.t}</h3>
                <p className="text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 bg-secondary text-secondary-foreground" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-extrabold mb-3">
                QUICK GRAM <span className="text-accent">TURA</span>
              </h3>
              <p className="text-sm opacity-90">
                Delivering groceries and essentials to rural towns with speed and trust.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm opacity-90">
                <li><Link to="/products" className="hover:opacity-100">All Products</Link></li>
                <li><Link to="/cart" className="hover:opacity-100">Cart</Link></li>
                <li><Link to="/admin" className="hover:opacity-100">Admin Panel</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Contact</h4>
              <p className="text-sm opacity-90 leading-relaxed">
                <a href={`tel:${SUPPORT_PHONE}`} className="hover:text-accent font-semibold">📞 +91 96035 40072</a><br />
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-accent">💬 WhatsApp</a><br />
                ✉️ hv30571@gmail.com
              </p>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-6 text-center text-sm opacity-70">
            © 2026 QUICK GRAM TURA. All rights reserved.
          </div>
        </div>
      </footer>
    </CustomerLayout>
  );
}
